import React, { useMemo } from 'react';
import './DiscordEmbedPreview.css';

const PREVIEW_CTX = {
  user: '@Jogador',
  username: 'jogador',
  member: 'Jogador',
  server: 'Nexus Origins',
  membercount: '1284',
  userid: '123456789012345678',
  serverid: '987654321098765432',
  avatar: 'https://cdn.discordapp.com/embed/avatars/1.png',
  ip: 'nexussmp.jogar.io',
  site: 'https://nexussmp.com.br/',
};

const SEPARATOR_MARKERS = ['{{separator}}', '────────────────────'];

function applyPlaceholders(text, ctx = PREVIEW_CTX) {
  if (!text) return '';
  return String(text)
    .replace(/\{user\}/gi, ctx.user)
    .replace(/\{username\}/gi, ctx.username)
    .replace(/\{member\}/gi, ctx.member)
    .replace(/\{server\}/gi, ctx.server)
    .replace(/\{membercount\}/gi, ctx.membercount)
    .replace(/\{userid\}/gi, ctx.userid)
    .replace(/\{serverid\}/gi, ctx.serverid)
    .replace(/\{avatar\}/gi, ctx.avatar)
    .replace(/\{ip\}/gi, ctx.ip)
    .replace(/\{site\}/gi, ctx.site);
}

/** Markdown leve para preview (negrito, itálico, code, links, blockquote, headings). */
function formatDiscordText(raw) {
  if (!raw) return null;
  const escaped = String(raw)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  let html = escaped
    .replace(/```(\w*)\n?([\s\S]*?)```/g, (_, _lang, code) => {
      return `<pre class="dex-codeblock"><code>${code.replace(/\n$/, '')}</code></pre>`;
    })
    .replace(/`([^`]+)`/g, '<code class="dex-inline">$1</code>')
    .replace(/^###\s+(.+)$/gm, '<strong class="dex-h3">$1</strong>')
    .replace(/^##\s+(.+)$/gm, '<strong class="dex-h2">$1</strong>')
    .replace(/^#\s+(.+)$/gm, '<strong class="dex-h1">$1</strong>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/_([^_]+)_/g, '<em>$1</em>')
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer">$1</a>'
    )
    .replace(/^&gt;\s?(.*)$/gm, '<span class="dex-quote">$1</span>')
    .replace(/\n/g, '<br />');

  return html;
}

function splitBySeparators(text) {
  let normalized = String(text || '');
  for (const marker of SEPARATOR_MARKERS) {
    const escaped = marker.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    normalized = normalized.replace(
      new RegExp(`\\n?\\s*${escaped}\\s*\\n?`, 'g'),
      '\n{{SEP}}\n'
    );
  }
  return normalized
    .split('{{SEP}}')
    .map((part) => part.replace(/^\n+|\n+$/g, '').trim())
    .filter(Boolean);
}

function resolvePreviewIcon(mode, customUrl, ctx) {
  switch (mode) {
    case 'user':
      return ctx.avatar;
    case 'server':
      return 'https://cdn.discordapp.com/embed/avatars/2.png';
    case 'bot':
      return 'https://cdn.discordapp.com/embed/avatars/0.png';
    case 'custom':
      return customUrl?.trim() || null;
    default:
      return null;
  }
}

const BUTTON_STYLE_CLASS = {
  Primary: 'dex-btn--primary',
  Secondary: 'dex-btn--secondary',
  Success: 'dex-btn--success',
  Danger: 'dex-btn--danger',
};

function PreviewButtons({ buttons }) {
  if (!buttons?.length) return null;
  return (
    <div className="dex-actions">
      {buttons.map((btn, i) => {
        const isLink =
          btn.isLink ||
          Boolean(String(btn.url || '').trim()) ||
          btn.style === 'Link';
        return (
          <span
            key={btn.id || `${btn.label}-${i}`}
            className={`dex-btn ${BUTTON_STYLE_CLASS[btn.style] || BUTTON_STYLE_CLASS.Secondary}${isLink ? ' dex-btn--link' : ''}`}
          >
            {btn.emoji ? <em aria-hidden="true">{btn.emoji}</em> : null}
            {btn.label}
            {isLink ? (
              <svg
                className="dex-btn__ext"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  fill="currentColor"
                  d="M14 3h7v7h-2V6.41l-9.29 9.3-1.42-1.42 9.3-9.29H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z"
                />
              </svg>
            ) : null}
          </span>
        );
      })}
    </div>
  );
}

/**
 * Preview visual de embed clássica ou Container Components V2.
 */
const DiscordEmbedPreview = ({
  embed,
  buttons,
  content = '',
  mentionUser = false,
  showEmbed = true,
  layout = 'classic',
  context = PREVIEW_CTX,
}) => {
  const isV2 = layout === 'v2' || layout === 'container';

  const resolved = useMemo(() => {
    if (!embed || !showEmbed) return null;
    const ctx = { ...PREVIEW_CTX, ...context };
    const title = applyPlaceholders(embed.title, ctx);
    const description = applyPlaceholders(embed.description, ctx);
    const authorName = applyPlaceholders(embed.authorName, ctx);
    const footer = applyPlaceholders(embed.footer, ctx);
    const color = embed.color || '#7C3AED';
    const accentEnabled = Boolean(embed.accentEnabled);
    const thumb = resolvePreviewIcon(
      embed.thumbnailMode || (embed.thumbnailUrl ? 'custom' : 'none'),
      applyPlaceholders(embed.thumbnailUrl, ctx),
      ctx
    );
    const authorIcon = resolvePreviewIcon(
      embed.authorIconMode || (embed.authorIconUrl ? 'custom' : 'none'),
      applyPlaceholders(embed.authorIconUrl, ctx),
      ctx
    );
    const footerIcon = resolvePreviewIcon(
      embed.footerIconMode || (embed.footerIconUrl ? 'custom' : 'none'),
      applyPlaceholders(embed.footerIconUrl, ctx),
      ctx
    );
    const imageUrl = applyPlaceholders(embed.imageUrl, ctx)?.trim() || null;
    const fields = (embed.fields || []).map((f) => ({
      name: applyPlaceholders(f.name, ctx),
      value: applyPlaceholders(f.value, ctx),
      inline: Boolean(f.inline),
    }));
    const textChunks = splitBySeparators(description);

    const hasBody =
      title ||
      description ||
      authorName ||
      footer ||
      imageUrl ||
      thumb ||
      fields.some((f) => f.name && f.value);

    if (!hasBody) return null;

    return {
      title,
      description,
      textChunks,
      authorName,
      footer,
      color,
      accentEnabled,
      thumb,
      authorIcon,
      footerIcon,
      imageUrl,
      fields,
      timestamp: Boolean(embed.timestamp),
      mention: ctx.user,
    };
  }, [embed, context, showEmbed]);

  const activeButtons = useMemo(() => {
    if (!buttons) return [];
    const list = Array.isArray(buttons)
      ? buttons
      : ['start', 'whitelist', 'link'].map((key) => buttons[key]).filter(Boolean);
    return list.filter((b) => b?.enabled !== false && b?.label).slice(0, 5);
  }, [buttons]);

  const contentHtml = useMemo(() => {
    const text = String(content || '').trim();
    if (!text) return null;
    return formatDiscordText(applyPlaceholders(text, { ...PREVIEW_CTX, ...context }));
  }, [content, context]);

  if (!resolved && !contentHtml && !activeButtons.length && !mentionUser) {
    return (
      <div className="dex">
        <p className="dex-empty">Configure a mensagem para ver a preview.</p>
      </div>
    );
  }

  return (
    <div className="dex" aria-label="Preview da mensagem no Discord">
      <div className="dex-chrome">
        <span className="dex-chrome__dot" />
        <span className="dex-chrome__label">
          Preview · Discord{isV2 ? ' · Container V2' : ''}
        </span>
      </div>

      <div className="dex-message">
        <img
          className="dex-avatar"
          src="https://cdn.discordapp.com/embed/avatars/0.png"
          alt=""
          width={40}
          height={40}
        />
        <div className="dex-body">
          <div className="dex-meta">
            <strong className="dex-bot-name">Nexus</strong>
            <span className="dex-bot-tag">APP</span>
            <time className="dex-time">Hoje às 13:45</time>
          </div>

          {mentionUser && (
            <p className="dex-content">
              <span className="dex-mention">
                {(context && context.user) || PREVIEW_CTX.user}
              </span>
            </p>
          )}

          {contentHtml && !isV2 && (
            <div
              className="dex-content"
              dangerouslySetInnerHTML={{ __html: contentHtml }}
            />
          )}

          {isV2 && (resolved || activeButtons.length > 0) && (
            <div
              className={`dex-container${resolved?.accentEnabled ? ' dex-container--accent' : ''}`}
              style={
                resolved?.accentEnabled
                  ? { borderColor: resolved.color }
                  : undefined
              }
            >
              {resolved?.imageUrl && (
                <div className="dex-container__banner">
                  <img src={resolved.imageUrl} alt="" />
                </div>
              )}

              <div className="dex-container__body">
                {resolved?.title && resolved?.textChunks?.length ? (
                  <>
                    <div
                      className="dex-container__text"
                      dangerouslySetInnerHTML={{
                        __html: formatDiscordText(
                          `## ${resolved.title}\n\n${resolved.textChunks[0]}`
                        ),
                      }}
                    />
                    {resolved.textChunks.slice(1).map((chunk, i) => (
                      <React.Fragment key={`sep-${i}`}>
                        <hr className="dex-separator" />
                        <div
                          className="dex-container__text"
                          dangerouslySetInnerHTML={{
                            __html: formatDiscordText(chunk),
                          }}
                        />
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  <>
                    {resolved?.title && (
                      <h3 className="dex-embed__title">{resolved.title}</h3>
                    )}
                    {resolved?.textChunks?.map((chunk, i) => (
                      <React.Fragment key={`chunk-${i}`}>
                        {i > 0 && <hr className="dex-separator" />}
                        <div
                          className="dex-container__text"
                          dangerouslySetInnerHTML={{
                            __html: formatDiscordText(chunk),
                          }}
                        />
                      </React.Fragment>
                    ))}
                  </>
                )}

                {resolved?.footer && (
                  <div className="dex-container__text dex-container__footer">
                    {resolved.footer}
                  </div>
                )}

                {activeButtons.length > 0 && (
                  <>
                    {(resolved?.title ||
                      resolved?.textChunks?.length ||
                      resolved?.imageUrl) && <hr className="dex-separator" />}
                    <PreviewButtons buttons={activeButtons} />
                  </>
                )}
              </div>
            </div>
          )}

          {!isV2 && resolved && (
            <div className="dex-embed" style={{ borderColor: resolved.color }}>
              <div className="dex-embed__grid">
                <div className="dex-embed__main">
                  {resolved.authorName && (
                    <div className="dex-embed__author">
                      {resolved.authorIcon && (
                        <img src={resolved.authorIcon} alt="" width={24} height={24} />
                      )}
                      <span>{resolved.authorName}</span>
                    </div>
                  )}

                  {resolved.title && (
                    <h3 className="dex-embed__title">{resolved.title}</h3>
                  )}

                  {resolved.description && (
                    <div
                      className="dex-embed__desc"
                      dangerouslySetInnerHTML={{
                        __html: formatDiscordText(resolved.description),
                      }}
                    />
                  )}

                  {resolved.fields.length > 0 && (
                    <div className="dex-embed__fields">
                      {resolved.fields.map((field, i) => (
                        <div
                          key={`${field.name}-${i}`}
                          className={`dex-embed__field${field.inline ? ' dex-embed__field--inline' : ''}`}
                        >
                          <div className="dex-embed__field-name">{field.name}</div>
                          <div
                            className="dex-embed__field-value"
                            dangerouslySetInnerHTML={{
                              __html: formatDiscordText(field.value),
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  {resolved.imageUrl && (
                    <div className="dex-embed__image">
                      <img src={resolved.imageUrl} alt="" />
                    </div>
                  )}

                  {(resolved.footer || resolved.timestamp) && (
                    <div className="dex-embed__footer">
                      {resolved.footerIcon && (
                        <img src={resolved.footerIcon} alt="" width={20} height={20} />
                      )}
                      <span>
                        {resolved.footer}
                        {resolved.footer && resolved.timestamp ? ' • ' : ''}
                        {resolved.timestamp ? 'Hoje às 13:45' : ''}
                      </span>
                    </div>
                  )}
                </div>

                {resolved.thumb && (
                  <img className="dex-embed__thumb" src={resolved.thumb} alt="" />
                )}
              </div>
            </div>
          )}

          {!isV2 && activeButtons.length > 0 && (
            <PreviewButtons buttons={activeButtons} />
          )}
        </div>
      </div>
    </div>
  );
};

export { applyPlaceholders, PREVIEW_CTX };
export default DiscordEmbedPreview;
